import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Brain, Share2, Play, TrendingUp, Cpu, Database, Workflow, ShieldCheck, ArrowUpRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/oraya-api";
import { IMG, PILLARS, TECH_PILLARS, INDUSTRIES } from "@/data/site";
import { Seo, Reveal, Overline, GlassCard, SectionHeader } from "@/components/site/primitives";
import { OButton } from "@/components/site/OButton";
import { SystemLoop } from "@/components/site/SystemLoop";
import { ArchitectureDiagram } from "@/components/site/ArchitectureDiagram";

const PILLAR_ICONS = [Brain, Share2, Play, TrendingUp];
const TECH_ICONS = [Cpu, Database, Workflow, ShieldCheck];

function statusLabel(s) {
  return s === "active" ? "Active System" : s === "coming_soon" ? "Coming Soon" : "Future System";
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const primary = products.find((p) => p.status === "active") || products[0];
  const future = products.filter((p) => p.status !== "active");

  return (
    <div>
      <Seo
        title="ORAYA — Building the Intelligence Layer for the Real World"
        description="ORAYA builds AI-native operating systems for complex real-world industries."
        image={IMG.globe}
      />

      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
        <div className="grain" />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[52vw] max-w-[680px] aspect-square pointer-events-none">
          <div className="globe-halo absolute inset-[-8%]" />
          <motion.img
            src={IMG.globe}
            alt="ORAYA glass intelligence globe — a translucent world visualization with connected nodes"
            className="hero-globe relative w-full h-full object-contain animate-float"
            style={{ maskImage: "radial-gradient(circle at 52% 50%, #000 46%, transparent 66%)", WebkitMaskImage: "radial-gradient(circle at 52% 50%, #000 46%, transparent 66%)" }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/70 to-transparent pointer-events-none" />

        <div className="oraya-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <Overline className="mb-8 block">AI Systems Company</Overline>
            <h1 className="display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] text-gradient-mist">
              Building the intelligence layer for the real world.
            </h1>
            <p className="text-lg md:text-xl text-subtle mt-8 max-w-2xl leading-relaxed">
              ORAYA builds AI-native operating systems for complex industries — connecting intelligence,
              data, workflows and execution into systems that understand, decide and deliver.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <OButton to="/systems" variant="primary" icon data-testid="hero-explore-btn">Explore our systems</OButton>
              <OButton to="/company" variant="secondary" data-testid="hero-discover-btn">Discover ORAYA</OButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ THE IDEA ============ */}
      <section className="oraya-container py-24 md:py-40">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-7">
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-ink">
              Software should not only help a business operate.
              <span className="block text-subtle mt-3">It should understand how the business works.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5 flex flex-col justify-end">
            <p className="text-lg text-subtle leading-relaxed">
              Traditional software divides complex businesses into disconnected tools. ORAYA connects
              intelligence, information, workflows, decisions and execution into one continuous system.
            </p>
            <Link to="/company" data-testid="idea-philosophy-link" className="group inline-flex items-center gap-2 text-ink mt-6 hover:text-white transition-colors">
              Our philosophy <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ INTELLIGENCE LOOP ============ */}
      <section className="oraya-container py-16 md:py-24">
        <Reveal>
          <SectionHeader overline="The ORAYA System" title="An intelligence loop, not a static tool." subtitle="Every ORAYA system runs a continuous loop — understanding context, generating intelligence, guiding decisions, executing work and learning from every outcome." />
        </Reveal>
        <Reveal delay={0.1}><SystemLoop /></Reveal>
      </section>

      {/* ============ BRAND PILLARS ============ */}
      <section className="oraya-container py-24 md:py-32">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <Reveal key={p.title} delay={i * 0.08}>
                <GlassCard hover className="p-8 h-full" data-testid={`pillar-${p.title.toLowerCase()}`}>
                  <Icon className="w-6 h-6 text-ink" strokeWidth={1.4} />
                  <h3 className="display text-xl font-medium text-ink mt-8">{p.title}</h3>
                  <p className="text-sm text-subtle mt-3 leading-relaxed">{p.text}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============ OUR SYSTEMS ============ */}
      <section className="oraya-container py-24 md:py-32">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Overline className="mb-5 block">Our Systems</Overline>
              <h2 className="display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] text-ink">
                Intelligent systems built for complex industries.
              </h2>
            </div>
            <Link to="/systems" data-testid="systems-viewall-link" className="group inline-flex items-center gap-2 text-subtle hover:text-ink transition-colors whitespace-nowrap">
              View all systems <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        {primary && (
          <Reveal delay={0.1}>
            <Link to={primary.cta_url || `/systems/${primary.slug}`} data-testid="home-constructons-card">
              <GlassCard hover className="relative overflow-hidden p-0 grid md:grid-cols-2">
                <div className="p-10 md:p-14 flex flex-col justify-between">
                  <div>
                    <Overline>{statusLabel(primary.status)}</Overline>
                    <h3 className="display text-3xl md:text-4xl font-medium text-ink mt-5">{primary.name}</h3>
                    <p className="text-ink/80 text-lg mt-3">{primary.tagline}</p>
                    <p className="text-subtle mt-5 leading-relaxed max-w-md">{primary.description}</p>
                  </div>
                  <span className="group inline-flex items-center gap-2 text-ink mt-10 font-medium">
                    {primary.cta_label || "Explore"} <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
                <div className="relative min-h-[280px] md:min-h-[420px]">
                  <img src={mediaUrl(primary.image) || IMG.constructons} alt={`${primary.name} system visualization`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-r from-surface/80 via-surface/20 to-transparent md:from-surface/70" />
                </div>
              </GlassCard>
            </Link>
          </Reveal>
        )}

        <div className="grid sm:grid-cols-3 gap-4 md:gap-6 mt-6">
          {future.slice(0, 3).map((p, i) => (
            <Reveal key={p.id || p.slug} delay={i * 0.08}>
              <GlassCard variant="medium" hover className="p-8 h-full" data-testid={`future-system-${i}`}>
                <Overline className="text-[10px]">{statusLabel(p.status)}</Overline>
                <h3 className="display text-xl font-medium text-ink mt-6">{p.name}</h3>
                <p className="text-sm text-subtle mt-2">{p.tagline}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ ARCHITECTURE ============ */}
      <section className="oraya-container py-24 md:py-32">
        <Reveal>
          <SectionHeader align="center" overline="ORAYA Intelligence Architecture" title="Intelligence that connects every part of the business." subtitle="AI models, a unified data layer, an intelligent workflow engine and a flexible integration layer — all orchestrated by the ORAYA Intelligence Core." />
        </Reveal>
        <Reveal delay={0.1}><ArchitectureDiagram /></Reveal>
      </section>

      {/* ============ TECHNOLOGY PILLARS ============ */}
      <section className="oraya-container py-16 md:py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {TECH_PILLARS.map((t, i) => {
            const Icon = TECH_ICONS[i];
            return (
              <Reveal key={t.tag} delay={i * 0.08}>
                <GlassCard variant="medium" hover className="p-8 h-full" data-testid={`tech-pillar-${i}`}>
                  <Icon className="w-6 h-6 text-ink" strokeWidth={1.4} />
                  <h3 className="text-sm font-semibold tracking-[0.18em] text-ink mt-8">{t.tag}</h3>
                  <p className="text-sm text-subtle mt-3 leading-relaxed">{t.text}</p>
                </GlassCard>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============ INDUSTRIES ============ */}
      <section className="oraya-container py-24 md:py-32">
        <Reveal>
          <SectionHeader overline="Industries" title="Built for the industries that power the world." />
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.name} delay={i * 0.06}>
              <div data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`} className={`relative rounded-2xl p-8 h-40 flex flex-col justify-between overflow-hidden hover-lift ${ind.active ? "glass-heavy border-hairline/15" : "glass-medium"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-ink">{ind.name}</span>
                </div>
                <span className={`text-xs uppercase tracking-[0.2em] ${ind.active ? "text-oraya-accent" : "text-subtle/60"}`}>
                  {ind.active ? "Active — ConstructONS.AI" : "Future System"}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ FUTURE VISION (always dark) ============ */}
      <section data-theme="dark" className="relative py-32 md:py-44 overflow-hidden border-y border-hairline/[0.06] bg-surface text-ink">
        <div className="grain" />
        <div className="oraya-container relative z-10 text-center">
          <Reveal>
            <h2 className="display text-4xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] text-gradient-mist">
              Don't build another tool.
              <span className="block mt-2">Build the system.</span>
            </h2>
            <p className="text-lg text-subtle mt-8 max-w-2xl mx-auto leading-relaxed">
              ORAYA is building the next generation of intelligent systems for the industries that power the real world.
            </p>
            <div className="mt-10">
              <OButton to="/contact" variant="primary" icon data-testid="future-cta-btn">Build what's next</OButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
